import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useFocusTrap } from '../lib/useFocusTrap.js'

export interface DialogProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  actions?: ReactNode
  /** Overrides the panel's default `min(440px, 100%)` — a confirm dialog never needs this; a library grid does. */
  width?: number | string
}

export function Dialog({ open, title, onClose, children, actions, width }: DialogProps) {
  const panel = useRef<HTMLDivElement>(null)

  useFocusTrap(open, panel)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  // On the body, so no card's own layer (an animation, a disabled button's
  // opacity) can paint over the backdrop.
  return createPortal(
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        ref={panel}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={width !== undefined ? { width } : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="dialog-title">{title}</h2>
        <div className="dialog-body">{children}</div>
        {actions ? <div className="dialog-actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
